import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useSocket } from './useSocket';
import { AppNotification, notificationsService } from '../services/api';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const PAGE_SIZE = 20;

// Merge by id so API pages and socket events never create duplicates.
function mergeById(existing: AppNotification[], incoming: AppNotification[]) {
  const byId = new Map<number, AppNotification>();
  existing.forEach((n) => byId.set(n.id, n));
  incoming.forEach((n) => {
    const current = byId.get(n.id);
    byId.set(n.id, current ? { ...n, read: current.read || n.read } : n);
  });
  return Array.from(byId.values()).sort((a, b) => b.id - a.id);
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef<number | null>(null);

  // Initial load via API
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
      cursorRef.current = null;
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    notificationsService
      .list({ limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setNotifications((prev) => mergeById(prev, res.notifications));
        setUnreadCount(res.unreadCount);
        setHasMore(res.hasMore);
        cursorRef.current = res.nextCursor;
      })
      .catch((err) => console.error('Failed to load notifications', err))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates via Socket.IO
  useEffect(() => {
    if (!socket) return;
    const onNew = (payload: { notification: AppNotification; unreadCount?: number }) => {
      const { notification, unreadCount: serverCount } = payload;
      setNotifications((prev) =>
        prev.some((n) => n.id === notification.id) ? prev : mergeById(prev, [notification])
      );
      if (typeof serverCount === 'number') setUnreadCount(serverCount);
    };
    socket.on('notification:new', onNew);
    return () => {
      socket.off('notification:new', onNew);
    };
  }, [socket]);

  const loadMore = useCallback(async () => {
    if (!hasMore || cursorRef.current === null) return;
    const res = await notificationsService.list({ limit: PAGE_SIZE, before: cursorRef.current });
    setNotifications((prev) => mergeById(prev, res.notifications));
    setHasMore(res.hasMore);
    cursorRef.current = res.nextCursor;
  }, [hasMore]);

  const markRead = useCallback(async (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      const res = await notificationsService.markRead(id);
      setUnreadCount(res.unreadCount);
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationsService.markAllRead();
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  }, []);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, isLoading, hasMore, loadMore, markRead, markAllRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
}
